import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import { projects } from '../Index';

const ProjectPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find the project that matches the slug
  const project = projects.find(p => p.url === `/${slug}`);

  if (!project) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background w-full overflow-x-hidden px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto py-16">
            <h1 className="text-4xl font-bold mb-4">Project Not Found</h1>
            <p className="text-muted-foreground mb-8">The project you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background w-full overflow-x-hidden px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto py-8 sm:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Project Header */}
            <div className="mb-6 sm:mb-8">
              <Badge variant="secondary" className="mb-4">{project.category}</Badge>
              <h1 className="text-3xl sm:text-4xl font-bold mb-4">{project.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {project.role && <span>Role: {project.role}</span>}
                {project.duration && <span>Duration: {project.duration}</span>}
                {project.status && <span>Status: {project.status}</span>}
              </div>
            </div>

            {/* Project Image */}
            <div className="mb-6 sm:mb-8 rounded-xl overflow-hidden">
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Project Description */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl font-semibold mb-4">Overview</h2>
              <p className="text-muted-foreground leading-relaxed">{project.description}</p>
            </div>

            {/* Impact Statement */}
            {project.impact && (
              <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-primary/5 rounded-xl border border-primary/10">
                <h2 className="text-2xl font-semibold mb-4">Impact</h2>
                <p className="text-primary font-medium">{project.impact}</p>
              </div>
            )}

            {/* Features */}
            {project.features && project.features.length > 0 && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-2xl font-semibold mb-4">Key Features</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg className="h-5 w-5 text-primary mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Technologies */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl font-semibold mb-4">Technologies Used</h2>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, i) => (
                  <Badge 
                    key={i} 
                    variant="outline" 
                    className="text-sm bg-white/5 border-primary/20 hover:bg-primary/10 transition-colors"
                  >
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Project Links */}
            <div className="flex flex-wrap gap-4">
              {project.liveDemo && (
                <Button asChild className="bg-gradient-to-r from-primary to-purple-500 hover:opacity-90 border-none">
                  <a href={project.liveDemo} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Live Demo
                  </a>
                </Button>
              )}
              {project.sourceCode && (
                <Button asChild variant="outline" className="border-primary/30 hover:bg-primary/10">
                  <a href={project.sourceCode} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                    <svg
                      className="h-4 w-4"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"></path>
                      <path d="M9 18c-4.51 2-5-2-7-2"></path>
                    </svg>
                    View Code
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProjectPage; 